import React, { useEffect, useState } from 'react';

import { Button } from 'components/ui/button';
import { BrokerError, BrokerPanel, deleteRecord, modifyRecordArray } from '../broker_titles/shared';
import TerritoryEditModal from './edit_modal';
import Pagination from 'components/ui/pagination';
import SearchInput from 'components/search_sidebar/sidebar_search';
import { get } from 'helpers/api';
import debounce from 'lodash/debounce';

export type GeoJsonPolygon = {
  type: 'Polygon';
  coordinates: number[][][]; // [ring][point][lng/lat]
} | {
  type: 'MultiPolygon';
  coordinates: number[][][][]; // [polygon][ring][point][lng/lat]
};

export type Territory = {
  id: number;
  countryId: number;
  state: string;
  county: string;
  label: string;
  displayLabel: string;
  canModify?: boolean;
  boundary?: GeoJsonPolygon;
}

export type TerritoriesTabProps = {
  googleMapsApiKey?: string;
}

const PAGE_LIMIT = 10;

export default function TerritoriesTab({ googleMapsApiKey }: TerritoriesTabProps) {
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [modalObject, setModalObject] = useState<{ isOpen: boolean; id: number | '' }>({ isOpen: false, id: '' });
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const searchTerritories = () => {
    get(`/territories?limit=${PAGE_LIMIT}&page=${page}&searchText=${searchTerm}`)
      .then((data) => {
        setTerritories(data.territories);
        setTotalPages(data.totalPages);
      })
      .catch(() => setError('Failed to search territories. Please try again.'));
  };

  useEffect(searchTerritories, [page]);

  useEffect(() => {
    const debouncedSearch = debounce(() => {
      setPage(1);
      searchTerritories();
    }, 500);

    debouncedSearch();
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm]);

  const openModal = (id: number | '') => setModalObject({ isOpen: true, id });
  const closeModal = () => setModalObject({ isOpen: false, id: '' });
  const territoryToEdit =
    typeof modalObject.id === 'number'
      ? territories.find(territory => territory.id === modalObject.id) || null
      : null;

  const handleFormSubmit = (newTerritory: Territory) => {
    modifyRecordArray(newTerritory, setTerritories, territories, 'label', modalObject, closeModal);
  };

  const deleteTerritory = ({ label, id }: { label?: string; id: number }) => {
    const displayLabel = label || 'Territory';
    deleteRecord(id, displayLabel, '/territories', 'Territory', setTerritories, setError);
  };

  const addNewTerritory = () => openModal('');

  return (
    <>
      <TerritoryEditModal
        googleMapsApiKey={googleMapsApiKey}
        isOpen={modalObject.isOpen}
        territory={territoryToEdit}
        onClose={closeModal}
        onSuccess={handleFormSubmit}
      />

      <div className="d-flex justify-content-between mb-2 align-items-center">
        <h5 className="fw-light m-0">Territories</h5>
        <Button label="Add Territory" type="button" onClick={addNewTerritory} />
      </div>

      <BrokerError error={error} />

      <div className="d-flex my-3">
        <SearchInput
          placeholder="Search Territories"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          onClear={() => setSearchTerm('')}
        />
      </div>

      <BrokerPanel
        cantModifyMessage="Corporate Territory"
        deleteRecord={deleteTerritory}
        firstColumnIdentifier="displayLabel"
        openModal={openModal}
        recordName="Territory"
        records={territories}
        secondColumnIdentifier=""
        onClick={addNewTerritory}
      />

      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onChange={(page: number) => setPage(page)}
      />
    </>
  );
}
